import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Settings as SettingsIcon, Save, Lock, Building2, Target, Calendar, Eye, EyeOff } from 'lucide-react';
import { logActivity } from '../lib/activity';
import { useTooltip } from '../lib/useTooltip';
import Skeleton from '../components/Skeleton';
import DataManagement from '../components/DataManagement';
import BackupManagement from '../components/BackupManagement';

interface Settings {
  id: string;
  company_name: string;
  email: string;
  weekly_revenue_goal: number;
  active_years: number[];
  profile_photo_url: string | null;
  show_fixed_expenses: boolean;
  show_tips: boolean;
  show_revenue_goal: boolean;
}

export default function Settings() {
  const { tooltip } = useTooltip();
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Settings>({
    id: '',
    company_name: '',
    email: '',
    weekly_revenue_goal: 1000,
    active_years: [],
    profile_photo_url: null,
    show_fixed_expenses: true,
    show_tips: false,
    show_revenue_goal: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [showHiddenLink, setShowHiddenLink] = useState(false);

  // Update years array to start from 2023
  const years = [2023, 2024, 2025, 2026, 2027, 2028];

  const toggleYear = (year: number) => {
    setSettings(prev => ({
      ...prev,
      active_years: prev.active_years.includes(year)
        ? prev.active_years.filter(y => y !== year)
        : [...prev.active_years, year].sort((a, b) => a - b)
    }));
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingSettings, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (!existingSettings) {
        const { data: newSettings, error: createError } = await supabase
          .from('settings')
          .insert([{
            user_id: user.id,
            company_name: '',
            email: user.email || '',
            weekly_revenue_goal: 1000,
            active_years: [2023, 2024, 2025],
          }])
          .select()
          .maybeSingle();

        if (createError) throw createError;
        if (newSettings) setSettings(newSettings);
      } else {
        setSettings(existingSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('settings')
        .update({
          company_name: settings.company_name,
          email: settings.email,
          weekly_revenue_goal: settings.weekly_revenue_goal,
          active_years: settings.active_years,
          show_fixed_expenses: settings.show_fixed_expenses,
          show_tips: settings.show_tips,
          show_revenue_goal: settings.show_revenue_goal,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Update email in auth if changed
      if (settings.email !== user.email) {
        const { error: updateEmailError } = await supabase.auth.updateUser({
          email: settings.email,
        });
        if (updateEmailError) throw updateEmailError;
      }

      setMessage({ type: 'success', text: 'Instellingen opgeslagen' });
      await logActivity('Instellingen bijgewerkt', 'Algemene instellingen zijn bijgewerkt');

      // Refresh the page to update all components
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Er is een fout opgetreden bij het opslaan' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangingPassword(true);
    setMessage(null);

    try {
      if (newPassword !== confirmPassword) {
        throw new Error('Nieuwe wachtwoorden komen niet overeen');
      }

      // First verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: settings.email,
        password: currentPassword,
      });

      if (signInError) {
        throw new Error('Huidig wachtwoord is onjuist');
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) throw updateError;

      setMessage({ type: 'success', text: 'Wachtwoord succesvol gewijzigd' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />

        <div className="space-y-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-gray-800 rounded-lg shadow p-6">
              <div className="space-y-4">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const handleLogoClick = () => {
    setClickCount(prev => {
      const newCount = prev + 1;
      if (newCount === 5) {
        setShowHiddenLink(true);
        return 0;
      }
      return newCount;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Instellingen</h1>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
            : 'bg-red-500/10 text-red-400 border border-red-500/20'
        }`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Building2
                className="h-5 w-5 text-gray-300 cursor-pointer"
                onClick={handleLogoClick}
              />
              <h2 className="text-base font-semibold text-white">
                Bedrijfsgegevens
              </h2>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Bedrijfsnaam
                </label>
                <input
                  type="text"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                    text-white bg-gray-800
                    focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  E-mailadres
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                    text-white bg-gray-800
                    focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
                />
              </div>
            </div>
            {showHiddenLink && (
              <div className="mt-4 text-right">
                <button
                  type="button"
                  onClick={() => navigate('/hidden')}
                  className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                >
                  Administratie
                </button>
              </div>
            )}
          </div>

          {/* Revenue Goals */}
          <div className="bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Target className="h-5 w-5 text-gray-300" />
              <h2 className="text-base font-semibold text-white">
                Omzetdoelen
              </h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Wekelijks omzetdoel (€)
              </label>
              <input
                type="number"
                min="0"
                step="100"
                value={settings.weekly_revenue_goal}
                onChange={(e) => setSettings({ ...settings, weekly_revenue_goal: parseFloat(e.target.value) })}
                {...tooltip('weekly-goal', 'Het bedrag dat je wekelijks wilt verdienen')}
                className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                  text-white bg-gray-800
                  focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
              />
            </div>
          </div>

          {/* Active Years Section */}
          <div className="bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="h-5 w-5 text-gray-300" />
              <h2 className="text-base font-semibold text-white">
                Actieve Jaren
              </h2>
            </div>

            <div>
              <p className="text-sm text-gray-400 mb-3">
                Selecteer de jaren waarvoor je omzet wilt bijhouden.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {years.map(year => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => toggleYear(year)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200
                      ${settings.active_years.includes(year)
                        ? 'bg-accent text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Feature Visibility Section */}
          <div className="bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Eye className="h-5 w-5 text-gray-300" />
              <h2 className="text-base font-semibold text-white">
                Zichtbaarheid Features
              </h2>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-400 mb-3">
                Bepaal welke features zichtbaar zijn in de applicatie.
              </p>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    Omzetdoel
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Toon het weekdoel card op het dashboard
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, show_revenue_goal: !settings.show_revenue_goal })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.show_revenue_goal ? 'bg-accent' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.show_revenue_goal ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    Vaste Lasten
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Toon vaste lasten in het menu
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, show_fixed_expenses: !settings.show_fixed_expenses })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.show_fixed_expenses ? 'bg-accent' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.show_fixed_expenses ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    Fooi
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Toon fooi in het menu
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, show_tips: !settings.show_tips })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.show_tips ? 'bg-accent' : 'bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.show_tips ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Data Management Section */}
          <DataManagement />

          {/* Backup Management Section */}
          <BackupManagement />

          {/* Security Section */}
          <div className="bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Lock className="h-5 w-5 text-gray-300" />
              <h2 className="text-base font-semibold text-white">
                Beveiliging
              </h2>
            </div>

            {!showPasswordForm ? (
              <div>
                <p className="text-sm text-gray-400 mb-3">
                  Wijzig je wachtwoord om je account te beveiligen.
                </p>
                <button
                  type="button"
                  onClick={() => setShowPasswordForm(true)}
                  className="w-full flex items-center justify-center px-3.5 py-2.5 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 hover:text-white transition-colors duration-200"
                >
                  Wachtwoord wijzigen
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Huidig wachtwoord
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                      text-white bg-gray-800
                      focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Nieuw wachtwoord
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                      text-white bg-gray-800
                      focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Bevestig nieuw wachtwoord
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full rounded-lg border border-gray-700 px-3.5 py-2.5 text-sm
                      text-white bg-gray-800
                      focus:border-accent focus:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-accent transition-all duration-200"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 hover:text-white transition-colors duration-200"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={handlePasswordChange}
                    disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                    className="flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 transition-colors duration-200"
                  >
                    {changingPassword ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Wijzigen...
                      </>
                    ) : (
                      'Wijzigen'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-accent hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 transition-colors duration-200"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Opslaan...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Opslaan
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}